import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Headset, X, MessageCircle, Video, Phone, Calendar, ArrowRight, Send, Clock, Check, ChevronLeft, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/lib/utils';
import { trackLead } from '@/lib/track';
import { getAttribution } from '@/lib/attribution';
import { useConsultationContext } from '@/lib/ConsultationContext';

const CHANNELS = [
  { id: 'chat', label: 'Live Chat', icon: MessageCircle, description: 'Text with a consultant now', color: '#C6A87C' },
  { id: 'video', label: 'Video Call', icon: Video, description: 'Face-to-face consultation', color: '#3B82F6' },
  { id: 'phone', label: 'Phone Call', icon: Phone, description: 'Speak with us directly', color: '#10B981' },
];

const LiveConsultation = () => {
  const location = useLocation();
  const { getSessionSummary, getCurrentModel, modelsViewed, zonesExplored } = useConsultationContext();
  const [isOpen, setIsOpen] = useState(false);

  // Listen for FAB menu event
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-consultation', handler);
    return () => window.removeEventListener('open-consultation', handler);
  }, []);
  const [view, setView] = useState('channels'); // channels, live-chat, schedule, summary, success
  const [repStatus, setRepStatus] = useState({ available: false, status: 'offline', message: '' });
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [contact, setContact] = useState({ name: '', email: '', phone: '', message: '' });
  const [scheduleData, setScheduleData] = useState({ date: '', time: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(() => `consult_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`);
  const messagesEndRef = useRef(null);

  // Check rep status periodically
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/consultations/rep-status`);
        setRepStatus(res.data);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('[LiveConsult] rep-status fetch failed (falling back to offline):', e);
      }
    };
    checkStatus();
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleChannelSelect = (channel) => {
    setSelectedChannel(channel);
    if (channel === 'chat' && repStatus.available) {
      setView('live-chat');
      setChatMessages([{
        id: 'welcome',
        sender: 'rep',
        text: `Hi! I can see you've been exploring our ${getCurrentModel()?.replace(/-/g, ' ') || 'van collection'}. How can I help you today?`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
    } else if (channel === 'phone' && repStatus.available) {
      // Direct call - still collect info for CRM
      setView('schedule');
    } else {
      setView('schedule');
    }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = { id: `u_${Date.now()}`, sender: 'user', text: chatInput.trim(), time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);

    // Send to backend chat API for bot response
    try {
      await axios.post(`${API}/chat`, { session_id: sessionId, sender: 'user', message: userMsg.text });
      const res = await axios.get(`${API}/chat/${sessionId}`);
      const lastBot = res.data.filter(m => m.sender === 'bot').pop();
      if (lastBot) {
        setChatMessages(prev => [...prev, { id: lastBot.id, sender: 'rep', text: lastBot.message, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
      }
    } catch {
      setChatMessages(prev => [...prev, { id: `err_${Date.now()}`, sender: 'rep', text: "I'm having a moment — please try again or schedule a call!", time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!contact.name || !contact.email) {
      toast.error('Please enter your name and email');
      return;
    }
    setIsSubmitting(true);
    try {
      const summary = getSessionSummary();
      const { data: createdConsultation } = await axios.post(`${API}/consultations`, {
        name: contact.name,
        email: contact.email,
        phone: contact.phone || null,
        channel: selectedChannel,
        session_summary: summary,
        current_page: location.pathname,
        current_model: getCurrentModel(),
        message: contact.message || null,
        preferred_date: scheduleData.date || null,
        preferred_time: scheduleData.time || null,
        attribution: getAttribution(),
      });
      setView('success');
      toast.success('Consultation request submitted!');
      trackLead('consultation_request', createdConsultation?.id, { form_position: 'consultation_modal' });
    } catch (error) {
      console.error('Consultation error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setView('channels');
    setSelectedChannel(null);
    setContact({ name: '', email: '', phone: '', message: '' });
    setScheduleData({ date: '', time: '' });
    setChatMessages([]);
  };

  const sessionSummary = getSessionSummary();

  if (!isOpen) {
    return (
      <button
        data-testid="consultation-toggle"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 bg-navy hover:bg-navy/90 text-white rounded-full shadow-xl hidden md:flex items-center gap-2 px-5 py-3 transition-all hover:scale-105 group"
      >
        <Headset className="w-5 h-5 text-gold" />
        <span className="font-medium text-sm">Live Consultation</span>
        <span className={`w-2.5 h-2.5 rounded-full ${repStatus.available ? 'bg-green-400' : 'bg-gray-400'}`} />
      </button>
    );
  }

  return (
    <div
      data-testid="consultation-widget"
      className="fixed bottom-6 left-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      style={{ maxHeight: 'calc(100vh - 140px)' }}
    >
      {/* Header */}
      <div className="bg-navy text-white px-5 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {view !== 'channels' && view !== 'success' && (
            <button onClick={() => view === 'live-chat' ? reset() : setView('channels')} className="p-1 hover:bg-white/10 rounded transition-colors mr-1">
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
            <Headset className="w-5 h-5 text-navy" />
          </div>
          <div>
            <h4 className="font-semibold text-sm">Live Consultation</h4>
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${repStatus.available ? 'bg-green-400' : 'bg-gray-400'}`} />
              <p className="text-xs text-white/70">{repStatus.available ? 'Consultant available' : 'Schedule a consultation'}</p>
            </div>
          </div>
        </div>
        <button data-testid="consultation-close" onClick={() => { setIsOpen(false); reset(); }} className="p-2 hover:bg-white/10 rounded-full transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {/* Channel Selection */}
        {view === 'channels' && (
          <div className="p-5 space-y-4">
            {/* Session Context Banner */}
            {(modelsViewed.length > 0 || zonesExplored.length > 0) && (
              <div data-testid="session-context-banner" className="bg-gold/5 border border-gold/20 rounded-xl p-3">
                <p className="text-xs font-semibold text-navy mb-1.5">Your Session</p>
                <div className="flex flex-wrap gap-1.5">
                  {modelsViewed.map(m => (
                    <Badge key={m} variant="outline" className="text-[10px] border-gold/30 text-navy">
                      {m.replace(/-/g, ' ')}
                    </Badge>
                  ))}
                  {zonesExplored.map(z => (
                    <Badge key={z} variant="outline" className="text-[10px] border-blue-200 text-blue-700">
                      {z}
                    </Badge>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  {sessionSummary.time_on_site_minutes} min on site | {sessionSummary.pages_visited.length} pages visited
                </p>
              </div>
            )}

            <p className="text-sm text-muted-foreground">How would you like to connect with our team?</p>

            {CHANNELS.map((channel) => {
              const Icon = channel.icon;
              const isAvailable = repStatus.available || channel.id !== 'chat';
              return (
                <button
                  key={channel.id}
                  data-testid={`channel-${channel.id}`}
                  onClick={() => handleChannelSelect(channel.id)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-100 hover:border-gold/40 hover:shadow-sm transition-all text-left group"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${channel.color}12` }}>
                    <Icon className="w-6 h-6" style={{ color: channel.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-navy text-sm">{channel.label}</span>
                      {channel.id === 'chat' && repStatus.available && (
                        <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">LIVE NOW</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{channel.description}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gold transition-colors" />
                </button>
              );
            })}

            {!repStatus.available && (
              <p className="text-xs text-center text-muted-foreground bg-surface rounded-lg p-3">
                <Clock className="w-3.5 h-3.5 inline mr-1" />
                {repStatus.message}
                {repStatus.next_available && <span className="block mt-1 font-medium text-navy">{repStatus.next_available}</span>}
              </p>
            )}
          </div>
        )}

        {/* Live Chat View */}
        {view === 'live-chat' && (
          <div className="flex flex-col" style={{ height: '380px' }}>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {chatMessages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] ${msg.sender === 'user' ? '' : 'flex gap-2'}`}>
                    {msg.sender === 'rep' && (
                      <div className="w-7 h-7 bg-navy rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <User className="w-3.5 h-3.5 text-gold" />
                      </div>
                    )}
                    <div>
                      <div className={`px-4 py-2.5 rounded-2xl text-sm ${
                        msg.sender === 'user'
                          ? 'bg-gold text-navy rounded-br-sm'
                          : 'bg-white text-navy shadow-sm rounded-bl-sm border'
                      }`}>
                        {msg.text}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1 px-1">{msg.time}</p>
                    </div>
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex gap-2">
                  <div className="w-7 h-7 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-3.5 h-3.5 text-gold" />
                  </div>
                  <div className="bg-white px-4 py-2.5 rounded-2xl rounded-bl-sm shadow-sm border">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); handleChatSend(); }}
              className="p-3 border-t bg-white flex gap-2"
            >
              <Input
                data-testid="consultation-chat-input"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1"
                disabled={chatLoading}
              />
              <Button
                data-testid="consultation-chat-send"
                type="submit"
                size="icon"
                disabled={!chatInput.trim() || chatLoading}
                className="bg-gold hover:bg-gold-hover text-navy"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        )}

        {/* Schedule / Contact Form */}
        {view === 'schedule' && (
          <div className="p-5 space-y-4">
            <div className="bg-surface rounded-xl p-3 flex items-center gap-3">
              {CHANNELS.find(c => c.id === selectedChannel) && (() => {
                const ch = CHANNELS.find(c => c.id === selectedChannel);
                const Icon = ch.icon;
                return (
                  <>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${ch.color}12` }}>
                      <Icon className="w-5 h-5" style={{ color: ch.color }} />
                    </div>
                    <div>
                      <p className="font-semibold text-navy text-sm">{ch.label} Consultation</p>
                      <p className="text-xs text-muted-foreground">{repStatus.available ? 'Connect now or schedule' : 'Schedule a time'}</p>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Session Summary Preview */}
            {(modelsViewed.length > 0 || zonesExplored.length > 0) && (
              <div className="bg-gold/5 border border-gold/15 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-navy tracking-wider mb-1">SESSION CONTEXT (shared with consultant)</p>
                <p className="text-xs text-muted-foreground">
                  {modelsViewed.length > 0 && `Models: ${modelsViewed.map(m => m.replace(/-/g, ' ')).join(', ')}. `}
                  {zonesExplored.length > 0 && `Zones: ${zonesExplored.join(', ')}. `}
                  {`${sessionSummary.time_on_site_minutes} min on site.`}
                </p>
              </div>
            )}

            <Input
              data-testid="consultation-name"
              placeholder="Your Name *"
              value={contact.name}
              onChange={(e) => setContact(prev => ({ ...prev, name: e.target.value }))}
            />
            <Input
              data-testid="consultation-email"
              type="email"
              placeholder="Email Address *"
              value={contact.email}
              onChange={(e) => setContact(prev => ({ ...prev, email: e.target.value }))}
            />
            <Input
              data-testid="consultation-phone"
              type="tel"
              placeholder="Phone Number"
              value={contact.phone}
              onChange={(e) => setContact(prev => ({ ...prev, phone: e.target.value }))}
            />

            {(!repStatus.available || selectedChannel === 'video') && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Preferred Date</label>
                  <Input
                    data-testid="consultation-date"
                    type="date"
                    value={scheduleData.date}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Preferred Time</label>
                  <select
                    data-testid="consultation-time"
                    value={scheduleData.time}
                    onChange={(e) => setScheduleData(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">Select time</option>
                    {['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <Textarea
              data-testid="consultation-message"
              placeholder="What would you like to discuss? (optional)"
              value={contact.message}
              onChange={(e) => setContact(prev => ({ ...prev, message: e.target.value }))}
              rows={3}
            />

            <Button
              data-testid="consultation-submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full bg-gold text-navy hover:bg-gold-hover font-semibold"
            >
              {isSubmitting ? 'Submitting...' : repStatus.available && selectedChannel !== 'video' ? 'Request Consultation' : 'Schedule Consultation'}
            </Button>

            <p className="text-[10px] text-muted-foreground text-center">
              Your browsing context will be shared to personalize your consultation.
            </p>
          </div>
        )}

        {/* Success View */}
        {view === 'success' && (
          <div className="p-8 text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-display text-xl font-bold text-navy">Request Received!</h3>
            <p className="text-sm text-muted-foreground">
              {selectedChannel === 'video'
                ? "We'll send you a video call link at your scheduled time."
                : selectedChannel === 'phone'
                ? "A consultant will call you shortly."
                : "A consultant will reach out to you soon."
              }
            </p>
            <p className="text-xs text-muted-foreground">
              Your session context has been shared so your consultant can pick up right where you left off.
            </p>
            <Button onClick={() => { setIsOpen(false); reset(); }} variant="outline" className="mt-4">
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveConsultation;
